"use client";
import React from "react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "../ui/button";
import { useMouseMovement } from "@/app/[lang]/MouseMovementContext";
import dynamic from "next/dynamic";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { languages } from "@/app/i18n/settings";
import { useTranslation } from "@/app/i18n/client";
const OrderSelection = dynamic(() => import("../selections/order-selection"), {
    ssr: false,
    loading: () => <LoadingComponent />,
});
import { ChevronDown } from "lucide-react";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import { useContext } from "react";
import { TagWithFullRelation } from "@/util/query/product-query";
import { ProductTag } from "../selections/order-selection";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Props {
    sortDataSource: string[];
    sortDefault: string;
    sortParamKey?: string; // custom query param name for sort
    pageParamKey?: string; // custom query param name for page (used to reset page when sorting changes)
    allTags?: TagWithFullRelation[] | ProductTag[];
}
interface LoadingProps {
    title?: string;
}

interface PageSizeProps {
    currentPageSize: number;
    defaultPageSize: number;
    pageSizeOptions: number[];
    pageSizeParamKey?: string;
    pageParamKey?: string;
}

const LoadingComponent = ({ title= "title-ascending" }: LoadingProps) => {
    const pathName = usePathname();
    const lng = languages.find((l) => pathName.startsWith(`/${l}`)) ?? "th";
    const translation: { [key: string]: { [key: string]: string } } = {
        en: {
            "manual": "Featured",
            "best-selling": "Best selling",
            "title-ascending": "Sort by A-Z",
            "title-descending": "Sort by Z-A",
            "price-ascending": "Price low to high",
            "price-descending": "Price high to low",
            "created-descending": "Sort by Newest - Oldest",
        },
        th: {
            "manual": "แนะนำ",
            "best-selling": "ขายดี",
            "title-ascending": "เรียงตามตัวอักษร A-Z",
            "title-descending": "เรียงตามตัวอักษร Z-A",
            "price-ascending": "ราคา จากต่ำไปสูง",
            "price-descending": "ราคา จากสูงไปต่ำ",
            "created-descending": "เรียงจากใหม่ล่าสุด",
        },
        zh: {
            "manual": "精选",
            "best-selling": "热销",
            "title-ascending": "按 A-Z 排序",
            "title-descending": "按 Z-A 排序",
            "price-ascending": "价格：从低到高",
            "price-descending": "价格：从高到低",
            "created-descending": "按最新 - 最旧排序",
        },
    };
    title = translation[lng][title as keyof typeof translation["en"]];
    const colorStyle = useContext(ColorStyleContext);
    return (
        <div
            className={cn(
                buttonVariants({ variant: "outline" }),
                "h-8 focus-visible:ring-0 focus-visible:ring-offset-0 gap-x-2 justify-center",
            )}
            style={{ color: colorStyle.text || "#3d3d3d" }}>
            {title}
            <ChevronDown className="h-4 w-4 opacity-90" />
        </div>
    );
};

export const CollectionListPageSizeSelectionClient = ({
    currentPageSize,
    defaultPageSize,
    pageSizeOptions,
    pageSizeParamKey = "page_size",
    pageParamKey = "page",
}: PageSizeProps) => {
    const { t } = useTranslation();
    const router = useRouter();
    const pathName = usePathname();
    const searchParams = useSearchParams();
    const colorStyle = useContext(ColorStyleContext);

    const handleOnValueChanged = (value: string) => {
        const params = new URLSearchParams(searchParams.toString());
        params.delete(pageParamKey);

        if (+value === defaultPageSize) {
            params.delete(pageSizeParamKey);
        } else {
            params.set(pageSizeParamKey, value);
        }

        const newPathName = `${pathName}?${params.toString()}`;
        router.push(newPathName, { scroll: false });
    };

    if (pageSizeOptions.length <= 1) return null;

    return (
        <div className="flex items-center gap-2">
            <span className="text-[16px] whitespace-nowrap" style={{ color: colorStyle.text || "#3d3d3d" }}>
                {t("product_list.products_per_page")}
            </span>
            <Select value={`${currentPageSize}`} onValueChange={handleOnValueChanged}>
                <SelectTrigger
                    className={cn("h-8 min-w-[92px] rounded-full border-input bg-transparent px-3 text-[16px]")}
                    style={{ color: colorStyle.text || "#3d3d3d" }}
                    aria-label={t("product_list.products_per_page")}
                >
                    <SelectValue placeholder={t("product_list.products_per_page")} />
                </SelectTrigger>
                <SelectContent>
                    <SelectGroup style={{ color: colorStyle.text || "#3d3d3d" }}>
                        {pageSizeOptions.map((pageSizeOption) => (
                            <SelectItem key={pageSizeOption} value={`${pageSizeOption}`}>
                                {t("product_list.products_per_page_option", { count: pageSizeOption })}
                            </SelectItem>
                        ))}
                    </SelectGroup>
                </SelectContent>
            </Select>
        </div>
    );
};

const CollectionListOrderSelectionClient = ({ sortDataSource, sortDefault, sortParamKey, pageParamKey, allTags }: Props) => {
    const { hasMouseMoved } = useMouseMovement();
    return hasMouseMoved ? (
        <OrderSelection
            className={cn(
                buttonVariants({ variant: "outline" }),
                "h-8 focus-visible:ring-0 focus-visible:ring-offset-0 gap-x-2 justify-center",
            )}
            sortDataSource={sortDataSource}
            sortDefault={sortDefault}
            sortParamKey={sortParamKey}
            pageParamKey={pageParamKey}
            allTags={allTags}
        />
    ) : (
        <LoadingComponent title={"title-ascending"} />
    );
};

export default CollectionListOrderSelectionClient;
